import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { List, ListHeader, ListRow } from "@d12labs/ui/data-list";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Pager } from "@d12labs/ui/pager";
import { SkeletonRow } from "@d12labs/ui/skeleton";
import { Toolbar } from "@d12labs/ui/toolbar";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { useEffect, useState } from "react";
// Shapes derive from the server's route defs (Phase 7) — no hand-typed Contact/Page.
import { crmApi } from "../api";

const PAGE_SIZE = 25;

const COLUMNS = [
	{ label: "Name", width: "1.4fr" },
	{ label: "Company", width: "1fr" },
	{ label: "Email", width: "1fr" },
	{ label: "Added", width: "100px", align: "right" as const },
];

function contactInitials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	return (
		((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || name.slice(0, 2).toUpperCase()
	);
}

export function ContactsListPage() {
	const search = useSearch({ strict: false }) as { q?: string; page?: number };
	const navigate = useNavigate();
	const q = search.q ?? "";
	const page = search.page ?? 1;

	// Debounce the input → URL update so we don't fetch on every keystroke.
	const [draftQ, setDraftQ] = useState(q);
	useEffect(() => setDraftQ(q), [q]);
	useEffect(() => {
		if (draftQ === q) return;
		const t = setTimeout(() => {
			navigate({
				to: "/crm",
				search: { q: draftQ, page: 1 }, // reset to page 1 on new search
			});
		}, 300);
		return () => clearTimeout(t);
	}, [draftQ, q, navigate]);

	const { data, isLoading } = useQuery({
		queryKey: ["crm", "contacts", { q, page }],
		// The def has no query schema (params are parsed in-handler) — the page's
		// param object rides typedRoute's free-form `query` field.
		queryFn: () =>
			crmApi.listContacts({
				query: { page, pageSize: PAGE_SIZE, ...(q ? { q } : {}) },
			}),
	});

	const total = data?.total ?? 0;
	const totalPages = data?.pageCount ?? 1;

	return (
		<>
			<PageHeader
				icon={Users}
				title="Contacts"
				subtitle="Everyone you've tracked. Search or filter to find anyone."
				badges={total > 0 ? <Badge tone="subtle">{total}</Badge> : undefined}
				actions={
					<Link to="/crm/contacts/new">
						<Button>New contact</Button>
					</Link>
				}
			/>

			<Toolbar
				left={
					<Input
						placeholder="Search name, email, or company"
						value={draftQ}
						onChange={(e) => setDraftQ(e.target.value)}
						className="h-7 w-64 text-body-sm"
					/>
				}
				right={
					data ? (
						<span className="text-meta text-ink-subtle">
							{total} result{total === 1 ? "" : "s"} · page {page} of {totalPages}
						</span>
					) : undefined
				}
			/>

			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-5xl">
					{isLoading ? (
						<List>
							<ListHeader columns={COLUMNS} />
							{Array.from({ length: 6 }).map((_, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
								<SkeletonRow key={i} columns={COLUMNS} />
							))}
						</List>
					) : data && data.items.length > 0 ? (
						<>
							<List>
								<ListHeader columns={COLUMNS} />
								{data.items.map((c) => (
									<ListRow
										key={c.id}
										cells={[
											{
												value: (
													<span className="flex items-center gap-2">
														<span className="flex size-5 flex-none items-center justify-center rounded-full border border-hairline bg-surface-2 text-[9px] font-medium text-ink">
															{contactInitials(c.name)}
														</span>
														<span className="truncate font-medium text-foreground">{c.name}</span>
													</span>
												),
												width: "1.4fr",
											},
											{ value: c.company ?? "—", width: "1fr", muted: true },
											{ value: c.email ?? "—", width: "1fr", muted: true },
											{
												value: new Date(c.createdAt).toLocaleDateString(undefined, {
													month: "short",
													day: "numeric",
												}),
												width: "100px",
												align: "right",
												muted: true,
											},
										]}
										render={(p) => (
											<Link
												to="/crm/contacts/$id"
												params={{ id: c.id }}
												className={p.className}
												style={p.style}
											>
												{p.children}
											</Link>
										)}
									/>
								))}
							</List>

							{totalPages > 1 && (
								<Pager
									className="mt-4 text-body-sm"
									page={page}
									pageCount={totalPages}
									onPageChange={(p) => navigate({ to: "/crm", search: { q, page: p } })}
									variant="ghost"
									label={
										<span className="text-ink-subtle">
											Page {page} of {totalPages}
										</span>
									}
								/>
							)}
						</>
					) : (
						<EmptyState
							icon={Users}
							title={q ? `No contacts match "${q}"` : "No contacts yet"}
							body={
								q
									? "Try a different search term or clear your search."
									: "Add your first contact to start tracking deals and conversations."
							}
							primary={
								q ? undefined : (
									<Link to="/crm/contacts/new">
										<Button>New contact</Button>
									</Link>
								)
							}
						/>
					)}
				</div>
			</PageBody>
		</>
	);
}
